/**
 * CARD STAGE — builds the real card in CSS 3D from the artist's geometry.
 *
 * Spec:  projects/pop-cards/docs/specs/2026-08-03-camera-configurator.md
 * Data:  assets/card-geometry.json, extracted from the artist's
 *        Meshes+Camera.jsx (3ds MAX -> MAX2AE -> After Effects).
 *
 * The seven planes are positioned by transforming each into CAMERA SPACE and
 * emitting a matrix3d. CSS `perspective` then does the projection, matching
 * the artist's camera exactly.
 *
 * Conventions were not guessed, and the obvious test is not good enough.
 * Scoring projected quads against a rendered frame can't separate the six
 * Euler orders: several of them put the card in roughly the right place, and
 * `zxy` passed that way before being shown wrong.
 *
 * What settles it is a constraint the card itself imposes. The four accordion
 * panels are ONE continuous strip of paper, so adjacent panels must share a
 * fold edge in 3D at every frame. Testing all 6 orders x 8 sign combinations
 * against that (scripts/verify-projection.py):
 *
 *     order=xyz signs=(1,1,1)  ->  max fold gap    0.04 units
 *     next best                ->  max fold gap  654.65 units   (torn card)
 *
 * on panels 1725 units wide, across all 101 frames. Independently, CoverFront
 * and CoverTop are the two faces of one sheet and coincide to 3.7px only under
 * an x-first order. Confirmed visually against the artist's render at both
 * ends of the animation.
 *
 * So: order xyz, all signs positive, AE +Z away from the viewer.
 *
 * ZOOM IS APPLIED OUTSIDE THE PERSPECTIVE (see .pp-zoom in stage.css). CSS
 * flattens the 3D result before magnifying it, so the card-to-camera
 * relationship never changes and the artist's multiply shading stays aligned
 * at every zoom level. This is why we crop rather than dolly.
 */

(function () {
	'use strict';

	// AE transform.orientation, composed R_x * R_y * R_z. See the header for
	// how this was pinned down — do not "simplify" it to another order.
	var ROT_ORDER = 'xyz';

	/**
	 * ⚠️ LAYOUT SCALE — the fix for the iOS crash of 2026-08-12. Read this
	 * before changing it, and never change it alone.
	 *
	 * The planes used to be laid out at the artist's geometry units: 2760x3675
	 * and 1725x2415 CSS px each, then scaled down to about 18% by the camera.
	 * A browser rasters a composited layer from its LAYOUT size, not its
	 * on-screen size, so the card was drawn at 18% and rastered at 200%.
	 * Measured on the live build at iPhone DPR: 166.7 megapixels of layer,
	 * 636 MB of raster, with the biggest single plane at 5521x7351. iOS kills
	 * a tab well below that, which is exactly what Daniel hit — "Can't open
	 * this page" in Chrome for iOS, twice, the moment the fourth photo mounted
	 * the stage.
	 *
	 * THE PROJECTION IS SCALE-INVARIANT, which is why this is safe. Divide
	 * every length by K — plane sizes, the matrix translation, the perspective
	 * — and the perspective divide is untouched:
	 *
	 *     (P/K) / ((P/K) - (z/K))  ==  P / (P - z)
	 *
	 * so every projected point simply lands at 1/K of where it did. The camera
	 * then fits the card by measuring its projected box (_fitU in
	 * configurator.js), so it derives a K-times-larger scale on its own and
	 * `-cx * scale` is unchanged. Same pixels, K² less raster.
	 *
	 * ⚠️ FOUR THINGS SHARE THIS SCALE and must move together:
	 *   1. the plane w/h, matrix translation and perspective — below
	 *   2. the corner radius, which is in geometry units — apply()
	 *   3. .pp-shade in stage.css, which is the comp square INSIDE the camera
	 *      — it reads --pp-comp, written by apply()
	 *   4. openVideoStyle() in configurator.js, which sizes the shading canvas
	 *      OUTSIDE the camera — it reads CardStage.LAYOUT_SCALE
	 * Anything inside a plane is already in cq units and scales itself.
	 *
	 * ⚠️ THE SCALE IS NOW DERIVED, NOT FIXED (2026-08-16). A hardcoded 10 was
	 * sized for the iPhone and quietly wrong everywhere else: the browser
	 * rasters each plane at LAYOUT size x DPR and the camera magnifies that
	 * bitmap back up, so on a big 1x monitor — layout smallest relative to the
	 * screen, no retina to hide behind — the photos and the R5 corners came out
	 * visibly pixelated. calibrate() picks the scale per device instead, from
	 * two constraints, and every length below reads the live value:
	 *
	 *   sharp   the largest plane's layout must cover the largest size a single
	 *           plane can reach on screen (the photo edit fits one panel to the
	 *           stage), with a little margin — raster >= pixels shown.
	 *   memory  the decoded raster is layout area x DPR² x 4 bytes, held as
	 *           long as the card is on screen. Budgeted at 128MB — under the
	 *           158MB the hero scroll measured as the stall ceiling, a fifth of
	 *           the 636MB that killed the tab on 2026-08-12. When sharp and
	 *           memory disagree, memory wins.
	 *
	 * Plane sizes come from the LOADED GEOMETRY, not constants here — the model
	 * has already been re-cut once (the 115mm half-card, 2026-08-16) and a
	 * hardcoded area would have gone stale with it.
	 */
	var LAYOUT_SCALE_MAX = 10;              // the old fixed value — now the ceiling
	var layoutScale = LAYOUT_SCALE_MAX;     // safe end until calibrate() runs

	var RASTER_BUDGET_BYTES = 128 * 1024 * 1024;
	var SHARP_MARGIN = 1.15;

	/**
	 * Pick the layout scale for this device. Returns true when the scale
	 * actually changed, so the caller knows to re-apply the current frame.
	 *
	 * Hysteresis: a fresh raster of all seven planes is the expensive thing
	 * this whole mechanism exists to control, so a recalibration within ~15%
	 * of the current scale keeps what it has. Dragging a window between
	 * monitors settles in one step; jittery resizes change nothing.
	 */
	function calibrate(geom, stageW, stageH, dpr) {
		if (!geom || !geom.planes || !stageW || !stageH) return false;
		var maxDim = 0, area = 0;
		geom.planes.forEach(function (p) {
			maxDim = Math.max(maxDim, p.size[0], p.size[1]);
			area += p.size[0] * p.size[1];
		});
		if (!maxDim) return false;
		dpr = dpr || 1;
		var kSharp = maxDim / (SHARP_MARGIN * Math.max(stageW, stageH));
		var kMem = Math.sqrt((area * dpr * dpr * 4) / RASTER_BUDGET_BYTES);
		var k = Math.min(LAYOUT_SCALE_MAX, Math.max(1, kSharp, kMem));
		var ratio = k / layoutScale;
		if (ratio > 0.85 && ratio < 1.18) return false;
		layoutScale = k;
		return true;
	}

	// ---- 4x4 matrix helpers, column-major on output for CSS ----------------

	function ident() {
		return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
	}

	function mul(a, b) {
		var o = new Array(16);
		for (var r = 0; r < 4; r++) {
			for (var c = 0; c < 4; c++) {
				o[r * 4 + c] =
					a[r * 4] * b[c] +
					a[r * 4 + 1] * b[4 + c] +
					a[r * 4 + 2] * b[8 + c] +
					a[r * 4 + 3] * b[12 + c];
			}
		}
		return o;
	}

	function rot(axis, deg) {
		var t = (deg * Math.PI) / 180,
			c = Math.cos(t),
			s = Math.sin(t),
			m = ident();
		if (axis === 'x') {
			m[5] = c; m[6] = -s; m[9] = s; m[10] = c;
		} else if (axis === 'y') {
			m[0] = c; m[2] = s; m[8] = -s; m[10] = c;
		} else {
			m[0] = c; m[1] = -s; m[4] = s; m[5] = c;
		}
		return m;
	}

	function translate(x, y, z) {
		var m = ident();
		m[3] = x; m[7] = y; m[11] = z;
		return m;
	}

	function orient(o) {
		var m = ident();
		for (var i = 0; i < ROT_ORDER.length; i++) {
			var axis = ROT_ORDER[i];
			m = mul(m, rot(axis, o['xyz'.indexOf(axis)]));
		}
		return m;
	}

	/** Inverse of a rotation+translation matrix. */
	function invertRigid(m) {
		var o = ident();
		o[0] = m[0]; o[1] = m[4]; o[2] = m[8];
		o[4] = m[1]; o[5] = m[5]; o[6] = m[9];
		o[8] = m[2]; o[9] = m[6]; o[10] = m[10];
		var tx = m[3], ty = m[7], tz = m[11];
		o[3] = -(o[0] * tx + o[1] * ty + o[2] * tz);
		o[7] = -(o[4] * tx + o[5] * ty + o[6] * tz);
		o[11] = -(o[8] * tx + o[9] * ty + o[10] * tz);
		return o;
	}

	/** Row-major -> CSS matrix3d (column-major), flipping Z for CSS's axis. */
	function toCss(m) {
		// S * M * S with S = diag(1,1,-1,1) converts AE's +Z-away to CSS's
		// +Z-toward-viewer without touching X or Y.
		var f = [
			m[0], m[1], -m[2], m[3],
			m[4], m[5], -m[6], m[7],
			-m[8], -m[9], m[10], -m[11],
			m[12], m[13], -m[14], m[15],
		];
		return [
			f[0], f[4], f[8], f[12],
			f[1], f[5], f[9], f[13],
			f[2], f[6], f[10], f[14],
			f[3], f[7], f[11], f[15],
		];
	}

	/**
	 * Rounded corners, in PLANE UNITS.
	 *
	 * ⚠️ THE MODEL IS EXACTLY 30 UNITS PER MILLIMETRE. Verified against the
	 * manufacturing spec (popup card spec A2) on every plane and both axes:
	 *     Part A base card   92 x 245 mm, folds to 92 x 122.5  -> 2760 x 3675
	 *     Part C 4-panel strip 80.5 x 230, so 57.5 x 80.5 each -> 1725 x 2415
	 * No rounding anywhere. Any millimetre in the spec becomes units by x30.
	 *
	 * So the spec's R5 is 5 x 30 = 150 units, exactly.
	 *
	 * This previously read 156.47, derived from the artist's blank templates
	 * being tagged 288 DPI, which implies an 88.2mm cover. The card is 92mm, so
	 * that route was 4.3% out — the templates carry the right PROPORTION but
	 * their DPI tag does not describe the finished card. Never infer physical
	 * size from an image's DPI metadata when a spec exists.
	 */
	var UNITS_PER_MM = 30;
	var CORNER_MM = 5;                                    // "corners R5", Parts A and C
	var CORNER_UNITS = CORNER_MM * UNITS_PER_MM;          // 150

	/* ⭐ PER-PLANE RADII FOR THE v2e RENDER (Yonatan, 17 Oct: "Strip corner radius: R4.75 confirmed, from the
	   production strip cut file (71.155 x 203.3, outer ends R4.75). Pages stay R7"). The render cuts those corners, so
	   the CSS planes must too, or a pale sliver of the shading shows past a rounder or squarer corner. Pages are the
	   four Cover planes (92 x 118.7 mm), the strip is the four wings. CORNER_UNITS stays for anything that still asks
	   for one radius (the selection ring). */
	var PAGE_CORNER_MM = 7, WING_CORNER_MM = 4.75;
	function cornerUnits(name) {
		return (WINGS.indexOf(name) !== -1 ? WING_CORNER_MM : PAGE_CORNER_MM) * UNITS_PER_MM;
	}

	/**
	 * ⚠️ ONLY THE OUTSIDE CORNERS. The spec, note 6: "Parts A and C: OUTSIDE
	 * corners rounded R5". Both parts are SINGLE pieces that get folded, not
	 * separate panels:
	 *
	 *   Part A  92 x 245, one score at the spine -> the two cover halves
	 *   Part C  80.5 x 230, three scores        -> the four accordion panels
	 *
	 * So a corner is only cut — and therefore only rounded — where it is not on
	 * a fold. Rounding every plane uniformly put a radius on the internal fold
	 * corners, which on the real card are continuous paper.
	 *
	 * Derived from the geometry rather than assumed: a corner is a FOLD corner
	 * if another, non-coincident plane has a corner at the same 3D point.
	 * CoverFront needs the physical match instead, because it is coincident with
	 * CoverTop (the two faces of one panel) and is mirrored left-to-right — its
	 * TL is CoverTop's TR, 9 units away, which is the paper's thickness.
	 *
	 * Order is CSS: top-left, top-right, bottom-right, bottom-left.
	 */
	// Flags, not px: the radius is divided by the LIVE layout scale in apply(),
	// like every other length — this is a radius on the plane's own box, and
	// that box is 1/K. Left unscaled it would be a 150px radius on a 172px
	// panel — the card would come out as lozenges.
	var R = 1, O = 0;
	var CORNERS = {
		CoverFront:  [R, R, O, O],   // spine along its bottom edge
		CoverTop:    [R, R, O, O],   // same panel, other face
		CoverBottom: [O, O, R, R],   // spine along its top edge
		CoverBack:   [O, O, R, R],   // the outside of the bottom page (v2e): same sheet as CoverBottom, top at the spine
		OutLeft1:    [R, O, O, R],   // outer end of the strip
		InLeft1:     [O, O, O, O],   // folds on both sides
		InRight1:    [O, O, O, O],
		OutRight1:   [O, R, R, O],   // other outer end
	};

	// The four accordion leaves, in the strip's own order. Named here because
	// the fold rule below is about these four and nothing else.
	var WINGS = ['OutLeft1', 'InLeft1', 'InRight1', 'OutRight1'];

	/**
	 * ⚠️ ONCE THE ACCORDION IS FOLDED FLAT, PAINT ORDER IS NOT DEPTH.
	 *
	 * Ported from scripts/build-hero-frames.py (collapsed(), 2026-08-16), which
	 * settled this against the artist's own pixels. The wings' fold angle runs
	 * 0 -> -90 deg across frames 0-38; at -90 the four panels are EXACTLY
	 * coincident — same position, same projected area, measured spread 4891
	 * units at frame 0 falling to 0.01 from frame 38 on. (The cover pair, by
	 * contrast, carries a real 9-unit paper thickness on all 101 frames. The
	 * accordion was modelled with none.)
	 *
	 * Coincident coplanar quads have no depth order, so preserve-3d interleaves
	 * them per pixel and you see one photo through another — Daniel, 2026-08-30.
	 * And depth cannot be repaired by nudging: the stacked wings project ~970
	 * units NEARER the camera than CoverFront, so painter's algorithm, reversed
	 * painter's and a per-pixel z-buffer alike all put them in front, wrongly.
	 *
	 * So the fold decides the order: backing -> wings -> cover. DOM order is the
	 * browser's tiebreaker once depths are equal, which is exactly the case this
	 * fires in.
	 *
	 * ⛔ FOLDED IS THE ANGLE BETWEEN THE LEAVES, NOT THE GAP BETWEEN THEIR
	 * CENTRES (Yonatan, 19 Oct: a photo still flashed on the folded strip, in
	 * Chrome and Safari, opening and closing). Until then this was "centres
	 * within 1% of the comp", tuned on the v2d fold, which jumped from 0 to
	 * 3.7 deg in one frame. v2e eases out of the fold instead — 0.6, 1.4,
	 * 2.6, 4.1, 6.0, 8.2 deg on frames 55 to 50, measured — so the 1% line
	 * fell at 1.4 deg, while the strip is still one thickness on screen, and
	 * the photo faces came on three frames before the leaves were apart.
	 * Yonatan's recording put "apart" at about 6 deg; FOLD_DEG holds the paper
	 * a frame past that. Degrees do not depend on the layout scale, the comp
	 * or how the bake eases, so the next geometry is judged the same way.
	 */
	var FOLD_DEG = 8;
	function collapsed(t, geom) {
		var ns = [], i, n;
		for (i = 0; i < WINGS.length; i++) {
			n = t.planes[WINGS[i]];
			if (n && n.normal) ns.push(n.normal);
		}
		if (ns.length < 2) return false;
		// Neighbours only: the strip folds at each crease, and a crease is
		// what opens. |cos| because a zig-zag's neighbours face opposite ways.
		var minCos = Math.cos(FOLD_DEG * Math.PI / 180);
		for (i = 0; i + 1 < ns.length; i++) {
			var a = ns[i], b = ns[i + 1];
			var la = Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]),
				lb = Math.sqrt(b[0] * b[0] + b[1] * b[1] + b[2] * b[2]);
			var c = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2]) / (la * lb);
			if (c < minCos) return false;
		}
		return true;
	}

	// The scene's authored child order, captured on first apply() so the open
	// pose can be put back exactly. Read from the DOM rather than listed here:
	// builder-stage.liquid owns what is in the scene, and a second list would
	// go stale the first time a plane is added.
	var authoredOrder = null, foldedNow = null;

	/**
	 * Put the scene in fold order (backing -> wings -> cover) or back in its
	 * authored order. Only touches the DOM when the state actually flips —
	 * this runs inside the per-frame callback.
	 */
	function setFoldOrder(scene, folded) {
		if (!scene) return;
		if (!authoredOrder) authoredOrder = [].slice.call(scene.children);
		if (folded === foldedNow) return;
		foldedNow = folded;

		var order;
		if (!folded) {
			order = authoredOrder;
		} else {
			var pick = function (sel) { return scene.querySelector(sel); };
			order = [pick('[data-pp-plane="CoverBack"]'), pick('[data-pp-plane="CoverBottom"]')];
			WINGS.forEach(function (n) {
				order.push(pick('[data-pp-plane-back="' + n + '"]'));
				order.push(pick('[data-pp-plane="' + n + '"]'));
			});
			order.push(pick('[data-pp-plane="CoverFront"]'),
				pick('[data-pp-plane="CoverTop"]'));
			// Anything the scene holds that is not a plane (the ground, a future
			// overlay) keeps its place by riding along at the end.
			authoredOrder.forEach(function (el) {
				if (order.indexOf(el) === -1) order.push(el);
			});
		}
		order.forEach(function (el) { if (el) scene.appendChild(el); });
	}



	// ---- public ------------------------------------------------------------

	/**
	 * Compute each plane's CSS transform for a given frame.
	 * @returns {{perspective:number, planes:Object<string,{matrix:string,w:number,h:number}>}}
	 */
	function frameTransforms(geom, frame) {
		var f = String(frame),
			cam = geom.camera,
			cx = geom.comp.width / 2,
			cy = geom.comp.height / 2;

		var cp = cam.position[f], co = cam.orientation[f];
		var camM = mul(translate(cp[0] - cx, cp[1] - cy, cp[2]), orient(co));

		// AE's focal length in pixels IS the CSS perspective — same pinhole.
		var P = cam.zoom[f][0];

		// ...but the two put the projection plane in different places, and
		// getting this wrong scales the whole card without otherwise looking
		// broken. AE projects onto a plane `zoom` units in front of the camera
		// (screen scale = zoom / depth). CSS projects onto z = 0 with the
		// viewer at z = +perspective (screen scale = P / (P - z)).
		//
		// So push the scene forward by P: a point at depth d lands on CSS
		// z = P - d, and P / (P - (P - d)) = P / d — exactly AE's scale.
		var view = mul(translate(0, 0, -P), invertRigid(camM));

		// ⚠️ The view matrix above is built in FULL geometry units — P included.
		// The scale is applied only when emitting, so none of the projection
		// maths above has to know about it. See the calibrate() note above.
		var K = layoutScale;

		var out = { perspective: P / K, planes: {} };

		geom.planes.forEach(function (p) {
			var pos = p.position[f] || p.position['0'];
			var o = p.orientation[f] || p.orientation['0'] || [0, 0, 0];
			var sc = p.scale[f] || p.scale['0'] || [100, 100, 100];

			var world = mul(translate(pos[0] - cx, pos[1] - cy, pos[2]), orient(o));
			var m = mul(view, world);
			var cm = toCss(m);
			var w = p.size[0] * (sc[0] / 100);
			var h = p.size[1] * (sc[1] / 100);

			// ── Apply the layout scale. Translation only: the rotation part of
			// the matrix is unitless and must NOT be touched, or the card
			// deforms. Indices 12/13/14 are the translation column in the
			// column-major form toCss() emits.
			cm[12] /= K; cm[13] /= K; cm[14] /= K;
			w /= K;
			h /= K;

			// Box comes from the scaled values, so every consumer — the camera's
			// _fitU, the opening's framing union — is consistently in the new
			// units and needs no change of its own.
			var Pk = P / K;
			var box = screenBox(cm, w, h, Pk);

			/* ── THE PERSPECTIVE IS BAKED IN HERE, NOT LEFT TO THE PARENT ──
			   CSS `perspective` on .pp-scene used to do this divide. WebKit
			   stopped honouring it: measured on an iPhone 2026-08-30, forcing
			   the property from 502px to 150px changed the painted pixels NOT
			   AT ALL, while every getBoundingClientRect stayed correct. So
			   layout and hit-testing had the projection and the paint did not,
			   and the panels — which are pushed back in z and rely on the
			   divide — painted about three times too wide and ran off both
			   edges of the phone. Chrome projected the same DOM correctly.

			   The property depends on a 3D rendering context surviving all the
			   way down, and .pp-plane itself declares `container-type` and
			   `overflow`, both of which flatten one (the note on .pp-plane in
			   stage.css says exactly this about backface-visibility). Rather
			   than hunt for whichever ancestor WebKit flattens on today, the
			   divide moves INTO each plane's own matrix, where it is just
			   arithmetic and there is no context left to lose.

			   A parent `perspective: P` with its origin on the child's own
			   transform-origin — which is this rig exactly, both are the scene
			   centre — is a premultiply by the perspective matrix. That matrix
			   is the identity with m[11] = -1/P, so the product only rewrites
			   row 3, and for a rigid M (row 3 = [0,0,0,1]) it is these four
			   terms. Column-major: row r, column c is cm[c * 4 + r].

			   ⚠️ AFTER screenBox, never before. screenBox does the same divide
			   itself (d = 1 - Z / P) to give the camera its framing box, and it
			   must be handed the UNBAKED matrix or it would apply it twice. */
			cm[3]  = -cm[2]  / Pk;
			cm[7]  = -cm[6]  / Pk;
			cm[11] = -cm[10] / Pk;
			cm[15] = 1 - cm[14] / Pk;
			out.planes[p.name] = {
				matrix: 'matrix3d(' + cm.map(function (v) { return v.toFixed(6); }).join(',') + ')',
				w: w,
				h: h,
				box: box,
				// The sheet's normal in camera space — the rigid part's z column,
				// unitless, so the layout scale and the baked divide (rows only)
				// leave it alone. collapsed() reads the fold from it.
				normal: [cm[8], cm[9], cm[10]],
				// ⚠️ SINGLE-SIDED. The artist, 2026-08-04: "you need to define
				// that only ONE FACE of the mesh is visible, and if its back is
				// to the camera it is simply transparent — you're seeing it
				// here as double-sided."
				//
				// Four of the seven planes turn away mid-unfold (InLeft1 at
				// frame 32, OutRight1 at 33, CoverFront/CoverTop at 57), and
				// CoverFront is ALREADY away at frame 0 — it and CoverTop are
				// the two sides of one physical panel, coincident with equal
				// and opposite area. Drawing both is what put photos on screen
				// that the artist's shading says are not there, which is what
				// "the shadow isn't married to the card" actually was.
				//
				// NOT left to backface-visibility: that was declared on the old
				// planes and demonstrably did nothing, because container-type
				// and overflow on the same element flatten the 3D context.
				// Decided here, in arithmetic, and verified against the render.
				facing: box.area > 0,
			};
		});

		return out;
	}

	/**
	 * Where a plane lands on screen, in unzoomed px relative to the scene
	 * centre — the same perspective divide the browser is about to do.
	 *
	 * The camera needs this to frame a plane. It must NOT get it by measuring
	 * getBoundingClientRect() and dividing by the current zoom: the zoom is
	 * transitioned, so a recompute mid-transition divides a partly-scaled rect
	 * by the target scale, and the next frame amplifies the error. That loop
	 * ran away to 248x on a resize. Computing it from the geometry is exact and
	 * has no feedback path.
	 */
	function screenBox(cm, w, h, P) {
		var hw = w / 2, hh = h / 2, i;
		var xs = [], ys = [], quad = [];
		var corners = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]];
		for (i = 0; i < 4; i++) {
			var lx = corners[i][0], ly = corners[i][1];
			var X = cm[0] * lx + cm[4] * ly + cm[12];
			var Y = cm[1] * lx + cm[5] * ly + cm[13];
			var Z = cm[2] * lx + cm[6] * ly + cm[14];
			var d = 1 - Z / P;
			xs.push(X / d);
			ys.push(Y / d);
			quad.push([X / d, Y / d]);
		}
		var x0 = Math.min.apply(null, xs), x1 = Math.max.apply(null, xs);
		var y0 = Math.min.apply(null, ys), y1 = Math.max.apply(null, ys);
		return {
			cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0,
			quad: quad, area: signedArea(quad),
		};
	}

	/**
	 * Twice the signed area of the projected quad. Its SIGN is the plane's
	 * facing: the winding of a quad reverses exactly when its normal crosses
	 * the view direction, so this is a per-frame backface test that needs no
	 * normals and no trust in CSS.
	 */
	function signedArea(q) {
		var a = 0;
		for (var i = 0; i < 4; i++) {
			var p = q[i], n = q[(i + 1) % 4];
			a += p[0] * n[1] - n[0] * p[1];
		}
		return a / 2;
	}

	window.PP = window.PP || {};
	window.PP.CardStage = {
		frameTransforms: frameTransforms,
		CORNER_UNITS: CORNER_UNITS,
		/* Which of each plane's four corners are rounded, in border-radius
		   order (TL, TR, BR, BL). Published so the selection ring can follow
		   the sheet instead of boxing it — see selPath() in configurator.js. */
		CORNERS: CORNERS,
		cornerUnits: cornerUnits,
		ROT_ORDER: ROT_ORDER,
		calibrate: calibrate,

		/** Apply a frame to the DOM. Elements are matched by data-pp-plane="<name>". */
		apply: function (root, geom, frame) {
			var t = frameTransforms(geom, frame);
			// ⛔ NO `perspective` ON THE SCENE ANY MORE — each plane's matrix
			// carries its own; see the note in frameTransforms. Cleared
			// explicitly rather than just left unset, because the stylesheet
			// may still declare one and it would double the divide.
			var scene = root.querySelector('[data-pp-scene]');
			if (scene) scene.style.perspective = 'none';

			// Paint order before paint: once the accordion is folded flat its four
			// leaves are exactly coincident and depth means nothing — see
			// collapsed(). No-ops on every frame but the two where the fold flips.
			var folded = collapsed(t, geom);
			setFoldOrder(scene, folded);

			// The comp square, in the same scaled units as the planes, for
			// .pp-shade — the artist's shading pass laid over the whole card
			// INSIDE the camera, which must register with it. CSS cannot read
			// LAYOUT_SCALE, so it is published here.
			//
			// ⚠️ On `root`, NOT on the scene: .pp-shade is the scene's SIBLING
			// inside .pp-zoom, and a custom property set on the scene would
			// never reach it. root is .pp-viewport, an ancestor of both.
			if (geom.comp && geom.comp.width) {
				root.style.setProperty('--pp-comp',
					(geom.comp.width / layoutScale) + 'px');
			}
			// The radius at the live scale — recomputed here, not at module
			// load, because calibrate() can change the scale after boot.
			Object.keys(t.planes).forEach(function (name) {
				var el = root.querySelector('[data-pp-plane="' + name + '"]');
				if (!el) return;
				var p = t.planes[name];
				var rPx = (cornerUnits(name) / layoutScale) + 'px';   // per plane since v2e: R7 pages, R4.75 strip

				/* ⛔ WHILE THE ACCORDION IS FOLDED, NO PHOTO FACE IS DRAWN AT
				   ALL — not the back-facing ones, not the front-facing ones.

				   The artist, 2026-08-04: only one face of the mesh is visible
				   and its back is transparent. Culling by facing implements
				   exactly that, and it is not enough here, because when the
				   card is shut two of the four leaves genuinely DO point their
				   photo at the camera — OutLeft1 and InRight1, measured. In
				   the real card those faces are pressed against the leaf in
				   front of them. On screen they are only hidden if something
				   paints over them, and the four leaves are EXACTLY coincident
				   while folded (centre-to-centre gap 0.00 from frame 100 to
				   38, measured; 26.4 at frame 37 when it starts to open). Two
				   coincident planes have no depth order, so which one survives
				   is the engine breaking a tie — and a tie breaks differently
				   when the transform stack changes, which is why this bug has
				   been fixed before and came back.

				   So it stops being a question. Folded means paper: every wing
				   shows its back and none shows its front, regardless of
				   facing. The fold order below still runs, but nothing now
				   depends on it winning. */
				var showFront = (folded && WINGS.indexOf(name) !== -1) ? false : p.facing;

				el.style.width = p.w + 'px';
				el.style.height = p.h + 'px';
				el.style.marginLeft = -p.w / 2 + 'px';
				el.style.marginTop = -p.h / 2 + 'px';
				el.style.transform = p.matrix;
				var c = CORNERS[name];
				el.style.borderRadius = c
					? c.map(function (f) { return f ? rPx : '0'; }).join(' ')
					: '0';
				// Cull, don't just hide the paint: a back-facing plane must not
				// hit-test either, or a photo you cannot see is still clickable.
				el.style.display = showFront ? '' : 'none';

				// ── AND THE OTHER SIDE OF THE SAME SHEET ──────────────────
				// Paper has two sides. Culling the photo face on its own left a
				// HOLE for as long as that face was turned away, and through
				// the hole you saw the next panel's photograph — "a pic on
				// both sides of the panel" (Daniel, 2026-08-29).
				//
				// The backing plane is the same quad with the same matrix, so
				// it lands exactly where the paper is, and it is displayed on
				// the INVERSE of `facing`. Exactly one of the pair is ever
				// drawn, which is why coincident geometry costs nothing here —
				// the same arrangement CoverFront/CoverTop has always had.
				var back = root.querySelector('[data-pp-plane-back="' + name + '"]');
				if (back) {
					back.style.width = p.w + 'px';
					back.style.height = p.h + 'px';
					back.style.marginLeft = -p.w / 2 + 'px';
					back.style.marginTop = -p.h / 2 + 'px';
					back.style.transform = p.matrix;
					// ⚠️ MIRRORED CORNERS. The reverse of a sheet is the front
					// flipped left-to-right, so its rounded corners swap sides;
					// leaving them unmirrored puts a square corner against a
					// round one at the fold, which reads as a nick in the paper.
					back.style.borderRadius = c
						? [c[1] ? rPx : '0', c[0] ? rPx : '0', c[3] ? rPx : '0', c[2] ? rPx : '0'].join(' ')
						: '0';
					back.style.display = showFront ? 'none' : '';
				}
			});
			return t;
		},
	};

	// A getter, so every consumer — compSpanCss() in configurator.js is the one
	// that matters — reads the CURRENT scale. A copied number would go stale
	// the moment calibrate() moved it, and the shading canvas would come out
	// sized for the wrong units.
	Object.defineProperty(window.PP.CardStage, 'LAYOUT_SCALE', {
		get: function () { return layoutScale; },
	});
})();
